import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const AdminMenu = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price: '',
    category: 'Matcha Drinks',
    image: '',
    isAvailable: true
  });
  const { token } = useAuth();

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get('/api/menu');
      setMenuItems(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching menu:', error);
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setEditForm({
      name: item.name,
      description: item.description,
      price: item.price
    });
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`/api/menu/${id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Menu item updated!');
      setEditingId(null);
      fetchMenuItems();
    } catch (error) {
      alert('Failed to update');
    }
  };

  const handleToggleAvailability = async (id, currentStatus) => {
    try {
      await axios.patch(`/api/menu/${id}/toggle`, 
        { isAvailable: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchMenuItems();
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/menu', newItem, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Menu item added successfully!');
      setShowAddForm(false);
      setNewItem({
        name: '',
        description: '',
        price: '',
        category: 'Matcha Drinks',
        image: '',
        isAvailable: true
      });
      fetchMenuItems();
    } catch (error) {
      alert('Failed to add item: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`/api/menu/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Item deleted successfully!');
      fetchMenuItems();
    } catch (error) {
      alert('Failed to delete item');
    }
  };

  if (loading) return <div style={styles.loading}>Loading menu...</div>;

  const categories = ['Matcha Drinks', 'Coffee', 'Sandwiches', 'Bowls', 'Refreshers', 'Signature Drinks'];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Manage Menu</h1>
          <p style={styles.subtitle}>Edit prices, availability, and add new items</p>
        </div>
        <button 
          onClick={() => setShowAddForm(!showAddForm)}
          style={styles.addButton}
        >
          {showAddForm ? 'Cancel' : '+ Add New Item'}
        </button>
      </div>

      {/* Add New Item Form */}
      {showAddForm && (
        <div style={styles.addFormContainer}>
          <h2 style={styles.formTitle}>Add New Menu Item</h2>
          <form onSubmit={handleAddItem} style={styles.form}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Item Name *</label>
                <input
                  type="text"
                  required
                  value={newItem.name}
                  onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., Matcha Latte"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Category *</label>
                <select
                  required
                  value={newItem.category}
                  onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                  style={styles.input}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Price (KES) *</label>
                <input
                  type="number"
                  required
                  value={newItem.price}
                  onChange={(e) => setNewItem({...newItem, price: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., 450"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description *</label>
              <textarea
                required
                value={newItem.description}
                onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                style={styles.textarea}
                placeholder="Describe the item..."
                rows="3"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Image URL (optional)</label>
              <input
                type="url"
                value={newItem.image}
                onChange={(e) => setNewItem({...newItem, image: e.target.value})}
                style={styles.input}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <button type="submit" style={styles.submitButton}>
              Add Menu Item
            </button>
          </form>
        </div>
      )}

      {/* Menu Items Table */}
      {categories.map(category => {
        const items = menuItems.filter(item => item.category === category);
        if (items.length === 0) return null;

        return (
          <div key={category} style={styles.categorySection}>
            <h2 style={styles.categoryTitle}>{category}</h2>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.th}>Image</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.th}>Price</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item._id} style={styles.tableRow}>
                      <td style={styles.td}>
                        <img 
                          src={item.image} 
                          alt={item.name}
                          style={styles.thumbnail}
                          onError={(e) => e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="50" height="50"%3E%3Crect fill="%23ddd" width="50" height="50"/%3E%3C/svg%3E'}
                        />
                      </td>
                      <td style={styles.td}>
                        {editingId === item._id ? (
                          <input 
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                            style={styles.input}
                          />
                        ) : (
                          <strong>{item.name}</strong>
                        )}
                      </td>
                      <td style={styles.td}>
                        {editingId === item._id ? (
                          <textarea
                            value={editForm.description}
                            onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                            style={styles.textarea}
                            rows="2"
                          />
                        ) : (
                          item.description
                        )}
                      </td>
                      <td style={styles.td}>
                        {editingId === item._id ? (
                          <input 
                            type="number"
                            value={editForm.price}
                            onChange={(e) => setEditForm({...editForm, price: e.target.value})}
                            style={styles.inputSmall}
                          />
                        ) : (
                          `KES ${item.price}`
                        )}
                      </td>
                      <td style={styles.td}>
                        <span style={item.isAvailable ? styles.statusAvailable : styles.statusUnavailable}>
                          {item.isAvailable ? 'Available' : 'Unavailable'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actions}>
                          {editingId === item._id ? (
                            <>
                              <button onClick={() => handleUpdate(item._id)} style={styles.btnSave}>Save</button>
                              <button onClick={() => setEditingId(null)} style={styles.btn}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => handleEdit(item)} style={styles.btn}>Edit</button>
                              <button 
                                onClick={() => handleToggleAvailability(item._id, item.isAvailable)}
                                style={item.isAvailable ? styles.btnUnavailable : styles.btnAvailable}
                              >
                                {item.isAvailable ? 'Mark Unavailable' : 'Mark Available'}
                              </button>
                              <button 
                                onClick={() => handleDelete(item._id)}
                                style={styles.btnDelete}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1800px', margin: '0 auto', padding: '2rem', background: '#fafafa', minHeight: '100vh' },
  loading: { textAlign: 'center', padding: '4rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '2.5rem', marginBottom: '0.5rem', color: '#1a5632', fontFamily: 'Playfair Display, serif' },
  subtitle: { color: '#5A5A5A', fontSize: '1.1rem' },
  addButton: { background: 'linear-gradient(135deg, #7AB845, #5A9634)', color: 'white', padding: '0.875rem 2rem', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(122, 184, 69, 0.3)' },
  addFormContainer: { background: 'white', padding: '2rem', borderRadius: '15px', marginBottom: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
  formTitle: { fontSize: '1.8rem', color: '#1a5632', marginBottom: '1.5rem', fontFamily: 'Playfair Display, serif' },
  form: { display: 'flex', flexDirection: 'column', gap: '1.5rem' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' },
  formGroup: { display: 'flex', flexDirection: 'column' },
  label: { marginBottom: '0.5rem', fontWeight: 600, color: '#2C2C2C' },
  input: { padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px', fontSize: '1rem' },
  textarea: { padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px', fontSize: '1rem', resize: 'vertical' },
  submitButton: { background: 'linear-gradient(135deg, #E75A7C, #D84967)', color: 'white', padding: '1rem 2rem', border: 'none', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(231, 90, 124, 0.3)' },
  categorySection: { marginBottom: '3rem' },
  categoryTitle: { fontSize: '1.8rem', color: '#E75A7C', marginBottom: '1rem', fontFamily: 'Playfair Display, serif' },
  tableContainer: { background: 'white', borderRadius: '15px', boxShadow: '0 2px 15px rgba(0,0,0,0.08)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#fafafa', borderBottom: '2px solid #e0e0e0' },
  th: { padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.9rem', color: '#2C2C2C' },
  tableRow: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '1rem', fontSize: '0.9rem', color: '#2C2C2C' },
  thumbnail: { width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' },
  inputSmall: { width: '100px', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' },
  statusAvailable: { background: '#e8f5e9', color: '#2e7d32', padding: '0.35rem 0.85rem', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600 },
  statusUnavailable: { background: '#ffebee', color: '#c62828', padding: '0.35rem 0.85rem', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600 },
  actions: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  btn: { background: 'white', color: '#2C2C2C', padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
  btnSave: { background: '#7AB845', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
  btnAvailable: { background: '#4CAF50', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
  btnUnavailable: { background: '#FF9800', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
  btnDelete: { background: '#F44336', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }
};

export default AdminMenu;